import { Box } from '@madeiramadeirabr/layout-web';
import { Text } from '@madeiramadeirabr/text-web';
import { RefObject, useCallback, useId, useLayoutEffect, useRef, useState } from 'react';

type TextCollapseProps = {
  maxHeight?: number;
  children?: JSX.Element;
};

function useCollapseController(maxHeight: number) {
  const panelRef = useRef<HTMLElement>(null);
  const [panelHeight, setPanelHeight] = useState<number | undefined>(undefined);
  const [expand, setExpand] = useState(false);

  const toggle = useCallback(() => {
    setExpand(current => !current);
  }, []);

  useLayoutEffect(() => {
    setPanelHeight(panelRef.current.scrollHeight);
  }, []);

  const hasSeeMore = panelHeight >= maxHeight;

  return { panelRef, expand, toggle, panelHeight, hasSeeMore };
}

function TextCollapse({ maxHeight = 135, children }: Readonly<TextCollapseProps>): JSX.Element {
  const id = useId();
  const { panelRef, expand, hasSeeMore, panelHeight, toggle } = useCollapseController(maxHeight);

  const idPanel = `${id}-panel`;
  const idTrigger = `${id}-trigger`;

  return (
    <Box
      css={{
        height: !expand ? `${maxHeight}px` : `calc(${panelHeight}px + $large)`,
        overflow: 'hidden',
        position: 'relative',
        my: '$small',
        transition: 'all .3s ease',
        '&:before': {
          content: '""',
          width: '100%',
          height: '110px',
          position: 'absolute',
          bottom: 0,
          background:
            // eslint-disable-next-line
            'linear-gradient(180deg, var(--colors-\\$background-brand-secondary-default) 0%, var(--colors-\\$background-neutral-default) 60%)',
          visibility: hasSeeMore && !expand ? 'visible' : 'hidden',
        },
      }}
    >
      <Box
        id={idPanel}
        aria-labelledby={idTrigger}
        css={{ pb: '$xlarge' }}
        ref={panelRef as RefObject<HTMLDivElement>}
      >
        {children}
      </Box>
      {hasSeeMore && (
        <Text
          as="button"
          id={idTrigger}
          aria-controls={idPanel}
          aria-expanded={expand}
          onClick={toggle}
          css={{
            color: '$text-focus',
            cursor: 'pointer',
            position: 'absolute',
            bottom: expand ? 0 : '20px',
            left: 0,
          }}
        >
          {expand ? 'Ler menos' : 'Ler mais'}
        </Text>
      )}
    </Box>
  );
}

export default TextCollapse;
